import { apiClient } from '../publicApiClient';
import { PassOfferSummary, PassTargetDetail, PassTargetType } from '../models/Pass';

/** Fetches target details for one type+id set. Missing targets are omitted by the API. */
export async function fetchPassTargets(
  targetType: PassTargetType,
  targetIds: (string | null | undefined)[],
): Promise<PassTargetDetail[]> {
  const uniqueIds = Array.from(new Set(targetIds.filter((id): id is string => Boolean(id))));
  if (uniqueIds.length === 0) {
    return [];
  }
  try {
    return await apiClient.listPassTargets({ targetType, targetIds: uniqueIds });
  } catch (error) {
    console.error('Error fetching pass target details:', error);
    return [];
  }
}

/** Index target details by id for offer/wallet rendering. */
export function indexPassTargets(targets: PassTargetDetail[]): Map<string, PassTargetDetail> {
  return new Map(targets.map((target) => [target.id, target]));
}

/**
 * Batch-load targets for mixed ROOM / LESSON_OPTION collections (wallet lists, offer cards).
 * Returns a single id → detail map; failed type batches contribute nothing.
 */
export async function fetchPassTargetsMap(
  items: Array<{ targetType: PassTargetType; targetIds?: string[] | null }>,
): Promise<Map<string, PassTargetDetail>> {
  const byType = new Map<PassTargetType, Set<string>>();
  for (const item of items) {
    const ids = item.targetIds ?? [];
    if (ids.length === 0) continue;
    let bucket = byType.get(item.targetType);
    if (!bucket) {
      bucket = new Set();
      byType.set(item.targetType, bucket);
    }
    ids.forEach((id) => {
      if (id) bucket!.add(id);
    });
  }

  const results = await Promise.all(
    Array.from(byType.entries()).map(([targetType, ids]) =>
      fetchPassTargets(targetType, Array.from(ids)),
    ),
  );

  return indexPassTargets(results.flat());
}

/** Display names for list summaries — known names only (no raw UUID spam). */
export function resolvePassTargetDisplayNames(
  targetIds: string[] | null | undefined,
  targetById: Map<string, PassTargetDetail> | Record<string, PassTargetDetail | undefined>,
): string[] {
  const ids = targetIds ?? [];
  const lookup =
    targetById instanceof Map
      ? (id: string) => targetById.get(id)
      : (id: string) => targetById[id];

  return ids
    .map((id) => lookup(id)?.name?.trim() || null)
    .filter((name): name is string => Boolean(name));
}

/**
 * Compact coverage line for pass list rows.
 * 1 → name; 2–3 → joined with · ; 4+ → first two + remainder count.
 */
export function formatPassTargetSummary(
  names: (string | null | undefined)[],
): string | null {
  const cleaned = names.map((name) => name?.trim()).filter((name): name is string => Boolean(name));
  if (cleaned.length === 0) return null;
  if (cleaned.length <= 3) return cleaned.join(' · ');
  return `${cleaned.slice(0, 2).join(' · ')} +${cleaned.length - 2}`;
}

/** Resolve + format in one step for list/table cells. */
export function passTargetSummaryFromIds(
  targetIds: string[] | null | undefined,
  targetById: Map<string, PassTargetDetail> | Record<string, PassTargetDetail | undefined>,
): string | null {
  return formatPassTargetSummary(resolvePassTargetDisplayNames(targetIds, targetById));
}

/** Display names for an offer/wallet row, preserving targetIds order; falls back to id when missing. */
export function resolvePassTargetNames(
  targetIds: string[] | null | undefined,
  targetById: Map<string, PassTargetDetail> | Record<string, PassTargetDetail | undefined>,
): string[] {
  const ids = targetIds ?? [];
  const lookup =
    targetById instanceof Map
      ? (id: string) => targetById.get(id)
      : (id: string) => targetById[id];

  return ids.map((id) => {
    const name = lookup(id)?.name?.trim();
    return name || id;
  });
}

/** Comma-joined target label for compact rows; empty when there are no ids. */
export function formatPassTargetLabel(names: string[]): string | null {
  const cleaned = names.map((name) => name.trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(', ') : null;
}

/** Current ROOM offers that can be spent on any of the given room ids. */
export function relatedRoomPassesForIds(
  offers: PassOfferSummary[],
  roomIds: Iterable<string>,
): PassOfferSummary[] {
  const ids = new Set(Array.from(roomIds).filter(Boolean));
  if (ids.size === 0) return [];
  return offers.filter(
    (offer) =>
      offer.targetType === 'ROOM' &&
      (offer.targetIds ?? []).some((targetId) => ids.has(targetId)),
  );
}
